/**
 * ESAL Constants Service
 *
 * Centralized loader for ESAL (Equivalent Single Axle Load) calculation constants.
 * Eliminates duplicate ESAL_CONSTANTS definitions in ESALTypes.ts and ESALTypesAircraft.ts.
 *
 * Original Issue: ESAL constants were duplicated in 2 files with only project-specific
 * defaults differing (DD, DL, analysis period, growth rate).
 *
 * Solution: Single JSON source with common constants + project-specific defaults.
 *
 * Data Source: src/app/data/esal-constants.json
 * Excel References: AASHTO 1993 Guide for Design of Pavement Structures
 *
 * @see esal-constants.json for constant values
 * @see esal-constants.schema.json for data validation schema
 */

import esalConstantsData from '../data/esal-constants.json';

/**
 * AASHTO Exponents used in ESAL calculations
 */
export interface AASHTOExponents {
    loadExponent: number; // 3.23 - Load exponent in beta calculation
    snExponent: number; // 5.19 - Structural number exponent in beta calculation
    ealfLoadExponent: number; // 4.79 - Load exponent in EALF calculation
    ealfAxleExponent: number; // 4.33 - Axle constant exponent in EALF calculation
}

/**
 * AASHTO Axle Type Constants
 */
export interface AxleConstants {
    steering: number; // 1
    single: number; // 1
    tandem: number; // 2
    tridem: number; // 3
}

/**
 * Common ESAL constants (shared across all project types)
 */
export interface CommonESALConstants {
    structuralNumber: number; // Default SN = 5.0
    presentServiceability: number; // Pt = 2.5
    knToKips: number; // 0.22481
    betaBase: number; // 0.4
    betaCoefficient: number; // 0.081
    logBase: number; // 19
    aashtoExponents: AASHTOExponents;
    axleConstants: AxleConstants;
}

/**
 * Project-specific default values
 */
export interface ProjectDefaults {
    directionalDistribution: number; // DD factor
    laneDistribution: number; // DL factor
    analysisPeriod: number; // years
    growthRate: number; // %
}

/**
 * Complete ESAL constants for a specific project type
 * Merges common constants with project-specific defaults
 */
export interface ESALConstants extends CommonESALConstants {
    defaultDD: number;
    defaultDL: number;
    defaultAnalysisPeriod: number;
    defaultGrowthRate: number;
}

/**
 * Project type identifier
 */
export type ProjectType = 'paved' | 'airport';

/**
 * ESAL Constants Service
 *
 * Provides type-safe access to ESAL calculation constants.
 * Loads from centralized JSON and merges common + project-specific values.
 */
export class ESALConstantsService {
    /**
     * Get complete ESAL constants for a specific project type
     *
     * @param projectType - 'paved' or 'airport'
     * @returns Complete ESAL constants including common + project-specific defaults
     *
     * @example
     * ```typescript
     * const pavedConstants = ESALConstantsService.getConstants('paved');
     * console.log(pavedConstants.defaultDD); // 0.5
     * console.log(pavedConstants.structuralNumber); // 5.0
     *
     * const airportConstants = ESALConstantsService.getConstants('airport');
     * console.log(airportConstants.defaultDD); // 1.0
     * ```
     */
    public static getConstants(projectType: ProjectType): ESALConstants {
        const common = esalConstantsData.common;
        const projectDefaults = esalConstantsData.projectDefaults[projectType];

        return {
            // Common constants (shared)
            structuralNumber: common.structuralNumber,
            presentServiceability: common.presentServiceability,
            knToKips: common.knToKips,
            betaBase: common.betaBase,
            betaCoefficient: common.betaCoefficient,
            logBase: common.logBase,
            aashtoExponents: { ...common.aashtoExponents },
            axleConstants: { ...common.axleConstants },

            // Project-specific defaults
            defaultDD: projectDefaults.directionalDistribution,
            defaultDL: projectDefaults.laneDistribution,
            defaultAnalysisPeriod: projectDefaults.analysisPeriod,
            defaultGrowthRate: projectDefaults.growthRate,
        };
    }

    /**
     * Get only common ESAL constants (no project-specific values)
     *
     * @returns Common ESAL constants shared across all project types
     */
    public static getCommonConstants(): CommonESALConstants {
        const common = esalConstantsData.common;

        return {
            structuralNumber: common.structuralNumber,
            presentServiceability: common.presentServiceability,
            knToKips: common.knToKips,
            betaBase: common.betaBase,
            betaCoefficient: common.betaCoefficient,
            logBase: common.logBase,
            aashtoExponents: { ...common.aashtoExponents },
            axleConstants: { ...common.axleConstants },
        };
    }

    /**
     * Get project-specific default values
     *
     * @param projectType - 'paved' or 'airport'
     * @returns Project-specific defaults (DD, DL, analysis period, growth rate)
     */
    public static getProjectDefaults(projectType: ProjectType): ProjectDefaults {
        const defaults = esalConstantsData.projectDefaults[projectType];

        return {
            directionalDistribution: defaults.directionalDistribution,
            laneDistribution: defaults.laneDistribution,
            analysisPeriod: defaults.analysisPeriod,
            growthRate: defaults.growthRate,
        };
    }

    /**
     * Get AASHTO exponents for ESAL calculations
     *
     * @returns AASHTO exponents object
     */
    public static getAASHTOExponents(): AASHTOExponents {
        return { ...esalConstantsData.common.aashtoExponents };
    }

    /**
     * Get axle type constants
     *
     * @returns Axle constants object
     */
    public static getAxleConstants(): AxleConstants {
        return { ...esalConstantsData.common.axleConstants };
    }

    /**
     * Get data source information
     *
     * @returns Metadata about the constants (version, source, last updated)
     */
    public static getMetadata(): {
        version: string;
        source: string;
        description: string;
        lastUpdated: string;
    } {
        return {
            version: esalConstantsData.version,
            source: esalConstantsData.source,
            description: esalConstantsData.description,
            lastUpdated: esalConstantsData.lastUpdated,
        };
    }

    /**
     * Validate that loaded constants are within expected ranges
     *
     * @returns Validation result with errors if any
     */
    public static validateConstants(): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];
        const common = esalConstantsData.common;

        // Validate common constants
        if (common.structuralNumber <= 0) {
            errors.push('Structural number must be positive');
        }

        if (common.presentServiceability < 0 || common.presentServiceability > 5) {
            errors.push('Present serviceability must be between 0 and 5');
        }

        if (common.knToKips <= 0) {
            errors.push('kN to kips conversion factor must be positive');
        }

        if (common.logBase <= 0) {
            errors.push('Log base must be positive');
        }

        // Validate exponents
        Object.entries(common.aashtoExponents).forEach(([key, value]) => {
            if (typeof value !== 'number' || value <= 0) {
                errors.push(`AASHTO exponent ${key} must be a positive number`);
            }
        });

        // Validate project defaults
        ['paved', 'airport'].forEach((projectType) => {
            const defaults = esalConstantsData.projectDefaults[projectType as ProjectType];

            if (defaults.directionalDistribution < 0 || defaults.directionalDistribution > 1) {
                errors.push(`${projectType}: Directional distribution must be between 0 and 1`);
            }

            if (defaults.laneDistribution < 0 || defaults.laneDistribution > 1) {
                errors.push(`${projectType}: Lane distribution must be between 0 and 1`);
            }

            if (defaults.analysisPeriod <= 0) {
                errors.push(`${projectType}: Analysis period must be positive`);
            }

            if (defaults.growthRate < 0) {
                errors.push(`${projectType}: Growth rate must be non-negative`);
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    /**
     * Get all available project types
     *
     * @returns Array of project type keys
     */
    public static getAvailableProjectTypes(): ProjectType[] {
        return Object.keys(esalConstantsData.projectDefaults) as ProjectType[];
    }

    /**
     * Check if a project type has defined defaults
     *
     * @param projectType - Project type to check
     * @returns true if project type exists in configuration
     */
    public static isProjectTypeSupported(projectType: string): projectType is ProjectType {
        return projectType in esalConstantsData.projectDefaults;
    }
}
